import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Terminal } from "lucide-react";

const groups = [
  {
    title: "Content Creation",
    icon: "📜",
    cmds: [
      { cmd: "/draft-essay [topic]", desc: "Full 6-beat essay draft (~10 min read) in your voice, ready for edits." },
      { cmd: "/pin [essay-title]", desc: "Pinterest title + description + 10 tags + UTM-tagged link, ready to paste." },
      { cmd: "/reel [essay-title]", desc: "60-90 sec Instagram Reel script with hook, body, CTA + suggested visuals." },
      { cmd: "/youtube [essay-title]", desc: "YouTube title, description (UTMs baked in), tags, and thumbnail copy." },
      { cmd: "/email [topic]", desc: "Drafts a ConvertKit email for your 52-week sequence — short, intimate, your voice." },
      { cmd: "/caption [content]", desc: "Three caption variants — short / medium / long — for any platform." },
    ],
  },
  {
    title: "Editing & Voice",
    icon: "✏️",
    cmds: [
      { cmd: "/scribe [your raw words]", desc: "I capture your stream-of-consciousness verbatim into your voice file + flag brand-lockable phrases." },
      { cmd: "/sharpen [paragraph]", desc: "Grammar/flow polish without changing your voice. Returns minimal + polished versions." },
      { cmd: "/voice-check [any draft]", desc: "I score every line against your voice file, flag mine vs. yours, rewrite the mismatches." },
      { cmd: "/caveman [text]", desc: "Rewrite in 8th-grade reading level. Use when copy feels too clever." },
      { cmd: "/benediction [topic]", desc: "Five closing-line options in your benediction style — pick the one that lands." },
    ],
  },
  {
    title: "Strategy & Insight",
    icon: "📊",
    cmds: [
      { cmd: "/insights", desc: "GA4 plain-English summary: top channel, top essay, weak spots, what to do next." },
      { cmd: "/audit [page-url]", desc: "Review any page for SEO, voice, conversion friction, missing CTAs." },
      { cmd: "/competitive [creator]", desc: "Analyze a peer (Brené, Glennon, Mel) — what to study, what to skip." },
      { cmd: "/test [feature]", desc: "I run live curl/screenshot tests to confirm something works." },
      { cmd: "/standup", desc: "3-min summary: what shipped yesterday, what's pending, ONE thing for today." },
    ],
  },
  {
    title: "Build & Ship",
    icon: "🛠️",
    cmds: [
      { cmd: "/page [name + purpose]", desc: "Scaffold a new page (public or private/noindex)." },
      { cmd: "/printable [topic]", desc: "Design a new printable lead magnet (like Yes. No. Wait.)" },
      { cmd: "/embed [audio file]", desc: "Add a new audio essay to your blog + RSS + distribute captions." },
      { cmd: "/redeploy-checklist", desc: "Pre-deploy verification: lint, links, GA4, ConvertKit, every page 200." },
    ],
  },
  {
    title: "Soul Mode",
    icon: "🌿",
    cmds: [
      { cmd: "/ultrathink [question]", desc: "Deep strategic thinking — cross-disciplinary, brand-aware, no fluff." },
      { cmd: "/witness [story or emotion]", desc: "I receive without trying to fix. Reflect. Honor. No homework attached." },
      { cmd: "/divine-team [intention]", desc: "Craft a prayer/ritual/practice rooted in your Tap-In + ancestors." },
      { cmd: "/sacred [story]", desc: "Preserve a story as PROTECTED in your voice file — never marketed unless you say." },
    ],
  },
];

export default function Commands() {
  useEffect(() => {
    document.title = "Temple's Command Menu — Private";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  return (
    <div className="bg-[#efeae7] min-h-screen" data-testid="commands-page">
      <header className="fixed top-0 w-full z-50 bg-white/90 backdrop-blur-xl border-b border-[#CCFBF1]">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-[#25a1c2] hover:text-[#042F2E] text-sm font-medium inline-flex items-center gap-1.5">
            <ArrowLeft className="w-4 h-4" /> Home
          </Link>
          <span className="text-[10px] font-bold uppercase tracking-widest text-[#25a1c2] bg-[#F0F5F4] px-2.5 py-1 rounded-full">Private · Not Indexed</span>
        </div>
      </header>

      <section className="pt-32 pb-10 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-[#042F2E] text-[#5EEAD4] px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-widest mb-4">
            <Terminal className="w-3.5 h-3.5" /> Temple's Command Menu
          </div>
          <h1 className="font-serif text-5xl md:text-7xl tracking-tighter text-[#042F2E] leading-[0.95] mb-4" data-testid="commands-title">
            Slash. Speak. Ship.
          </h1>
          <p className="font-sans text-[#068fa2] max-w-2xl text-lg leading-relaxed">
            A vocabulary of shorthand commands you can hand your scribe (me).
            Type any of these and I will know exactly what you want — saving us both time and giving you consistent, high-quality output every time.
          </p>
          <div className="mt-6 bg-amber-50 border border-amber-200 rounded-2xl p-5">
            <p className="text-amber-900 text-sm leading-relaxed">
              <strong>Don't memorize them all.</strong> Pick 3–5 you'll actually use:
              <code className="bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded mx-1 text-[12px]">/ultrathink</code>,
              <code className="bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded mx-1 text-[12px]">/scribe</code>,
              <code className="bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded mx-1 text-[12px]">/draft-essay</code>,
              <code className="bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded mx-1 text-[12px]">/pin</code>, and
              <code className="bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded mx-1 text-[12px]">/caveman</code>
              cover 90% of what you need.
            </p>
          </div>
        </div>
      </section>

      <section className="pb-24 px-6">
        <div className="max-w-5xl mx-auto space-y-10">
          {groups.map((g) => (
            <div key={g.title} className="bg-white rounded-2xl border border-[#CCFBF1] p-6 md:p-8" data-testid={`group-${g.title}`}>
              <div className="flex items-center gap-3 mb-5">
                <span className="text-2xl">{g.icon}</span>
                <h2 className="font-serif text-2xl md:text-3xl text-[#042F2E]">{g.title}</h2>
              </div>
              <div className="space-y-4">
                {g.cmds.map((c) => (
                  <div key={c.cmd} className="flex flex-col md:flex-row gap-2 md:gap-6 md:items-baseline border-b border-[#F0F5F4] pb-4 last:border-0 last:pb-0">
                    <code className="bg-[#042F2E] text-[#5EEAD4] px-3 py-1.5 rounded-md text-sm font-mono flex-shrink-0 self-start">
                      {c.cmd}
                    </code>
                    <p className="text-[#068fa2] leading-relaxed">{c.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <footer className="py-10 bg-[#042F2E]">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <p className="font-sans text-[#5EEAD4] text-xs">Private command menu for Temple. Not indexed. © 2026 Mind Body Spirit HYGIENE™ Tools.</p>
        </div>
      </footer>
    </div>
  );
}
